import React, { useEffect, useRef, useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  ScrollView,
  Platform,
  ActivityIndicator,
  StyleSheet,
} from 'react-native';
import { Picker } from '@react-native-picker/picker';
import Checkbox from 'expo-checkbox';
import { FontAwesome5 } from '@expo/vector-icons';
import * as XLSX from 'xlsx';

import { BasicModalContainer, ModalHeader, ModalBody, ModalFooter, commonModalStyle } from '../../../../common/components/basicmodal';
import { BOHButton } from '../../../../common/components/bohtoolbar';
import { useAlertModal } from '../../../../common/hooks';
import { importProducts } from '../../../../api/Product';
import { getPriceGroupsData } from '../../../../api/Price';
import { FIELD_TYPE_LABELS } from '../../../settings/inventoryfields/InventoryFields';

// Importable core product fields presented in the column matcher.
const CORE_TARGETS = [
  { value: 'barcode', label: 'Barcode (match key)' },
  { value: 'tracking_tag', label: 'Tracking Tag' },
  { value: 'license_number', label: 'License #' },
  { value: 'item_id', label: 'Item ID' },
  { value: 'size', label: 'Size' },
  { value: 'quantity', label: 'Quantity' },
  { value: 'item_starting_number', label: 'Item Starting Number' },
  { value: 'status', label: 'Status (name or number)' },
  { value: 'category', label: 'Category (name)' },
  { value: 'family', label: 'Family (name)' },
  { value: 'line', label: 'Line (name)' },
  { value: 'price_group', label: 'Price Group (for new lines)' },
  { value: 'home_location', label: 'Home Location (name)' },
  { value: 'current_location', label: 'Current Location (name)' },
];

const normalizeHeader = (h: string) =>
  String(h || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

// Best-effort automatic mapping of an incoming column header to a target.
const guessTarget = (header: string, metaFields: any[]) => {
  const n = normalizeHeader(header);
  if (!n) return '';
  const alias: Record<string, string> = {
    barcode: 'barcode',
    bar_code: 'barcode',
    tracking_tag: 'tracking_tag', tracking_tags: 'tracking_tag', tag: 'tracking_tag', tags: 'tracking_tag',
    license_number: 'license_number', license: 'license_number', license_no: 'license_number', license_num: 'license_number',
    item_id: 'item_id', itemid: 'item_id',
    size: 'size',
    quantity: 'quantity', qty: 'quantity',
    item_starting_number: 'item_starting_number', starting_number: 'item_starting_number',
    status: 'status',
    category: 'category',
    family: 'family',
    line: 'line',
    home_location: 'home_location', home: 'home_location',
    current_location: 'current_location', location: 'current_location',
  };
  if (alias[n]) return alias[n];
  const meta = (metaFields || []).find(
    f => normalizeHeader(f.label) === n || normalizeHeader(f.field_key) === n
  );
  if (meta) return `meta:${meta.id}`;
  return '';
};

const ImportProductsModal = ({ isModalVisible, productFields, closeModal, onImported }) => {
  const { showAlert } = useAlertModal();
  const fileInputRef = useRef<any>(null);

  const [fileName, setFileName] = useState('');
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<any[]>([]);
  const [mappings, setMappings] = useState<any[]>([]);
  const [options, setOptions] = useState({ updateExisting: true, createNew: false, metaOnly: false, createStructure: true });
  const [priceGroups, setPriceGroups] = useState<any[]>([]);
  const [defaultPriceGroup, setDefaultPriceGroup] = useState<any>(null);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [result, setResult] = useState<any>(null);

  useEffect(() => {
    if (isModalVisible) {
      setFileName('');
      setHeaders([]);
      setRows([]);
      setMappings([]);
      setResult(null);
      setProgress(null);
      setOptions({ updateExisting: true, createNew: false, metaOnly: false, createStructure: true });
      setDefaultPriceGroup(null);
      getPriceGroupsData((jsonRes, status) => {
        if (status === 200 && Array.isArray(jsonRes)) setPriceGroups(jsonRes);
      });
    }
  }, [isModalVisible]);

  const handleFile = (e: any) => {
    const file = e?.target?.files?.[0];
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (ev: any) => {
      try {
        const wb = XLSX.read(ev.target.result, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const json: any[] = XLSX.utils.sheet_to_json(ws, { defval: '' });
        if (!json.length) {
          showAlert('error', 'No rows found in the first sheet');
          return;
        }
        const cols = Object.keys(json[0]);
        setHeaders(cols);
        setRows(json);
        setMappings(cols.map(c => ({ column: c, target: guessTarget(c, productFields), newType: 'text', newLabel: c })));
      } catch (err) {
        console.error(err);
        showAlert('error', 'Could not parse that file');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const updateMapping = (column: string, patch: any) => {
    setMappings(prev => prev.map(m => (m.column === column ? { ...m, ...patch } : m)));
  };

  const runImport = async () => {
    const active = mappings.filter(m => m.target);
    if (!active.length) {
      showAlert('error', 'Map at least one column before importing');
      return;
    }
    if (!options.updateExisting && !options.createNew) {
      showAlert('error', 'Choose at least one import action (update or create)');
      return;
    }
    const barcodeMapped = mappings.some(m => m.target === 'barcode');
    if (options.updateExisting && !barcodeMapped) {
      showAlert('error', '"Update existing" requires a column mapped to Barcode');
      return;
    }

    // Send rows in chunks — large payloads get killed by the web server's
    // body-size limit (shows up as a CORS/network error) and single huge
    // requests can hit gateway timeouts.
    const CHUNK = 400;
    const chunks: any[][] = [];
    for (let i = 0; i < rows.length; i += CHUNK) chunks.push(rows.slice(i, i + CHUNK));

    const basePayload = {
      options: { ...options, default_price_group: defaultPriceGroup },
      mappings: active.map(m => ({
        column: m.column,
        target: m.target === 'new' ? `new:${m.newType}` : m.target,
        newLabel: m.target === 'new' ? m.newLabel : undefined,
      })),
    };

    const callImport = (chunkRows: any[]) =>
      new Promise<{ jsonRes: any; status: number }>((resolve) => {
        importProducts({ ...basePayload, rows: chunkRows }, (jsonRes, status) => {
          resolve({ jsonRes, status });
        });
      });

    const aggregated: any = {
      created: 0, updated: 0, skipped: 0,
      errors: [] as any[],
      newFields: [] as any[],
      structureCreated: { categories: [] as string[], families: [] as string[], lines: [] as string[] },
    };

    setImporting(true);
    setProgress({ done: 0, total: chunks.length });
    try {
      for (let i = 0; i < chunks.length; i++) {
        const { jsonRes, status } = await callImport(chunks[i]);
        if (status !== 200) {
          showAlert('error', jsonRes?.error || `Import failed on batch ${i + 1}`);
          setImporting(false);
          return;
        }
        aggregated.created += jsonRes.created || 0;
        aggregated.updated += jsonRes.updated || 0;
        aggregated.skipped += jsonRes.skipped || 0;
        (jsonRes.errors || []).forEach((e: any) => {
          aggregated.errors.push({ row: (e?.row ?? 0) > 0 ? e.row + i * CHUNK : e?.row, error: e?.error });
        });
        aggregated.newFields.push(...(jsonRes.newFields || []));
        const sc = jsonRes.structureCreated;
        if (sc) {
          aggregated.structureCreated.categories.push(...(sc.categories || []));
          aggregated.structureCreated.families.push(...(sc.families || []));
          aggregated.structureCreated.lines.push(...(sc.lines || []));
        }
        setProgress({ done: i + 1, total: chunks.length });
      }
      setResult(aggregated);
      if (onImported) onImported();
    } catch (e) {
      showAlert('error', 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const sampleFor = (col: string) =>
    rows.slice(0, 3).map(r => String(r[col] ?? '')).filter(Boolean).join(', ');

  const body = () => {
    if (result) {
      return (
        <View>
          <Text style={styles.resultTitle}>Import complete</Text>
          <Text>Created: {result.created}</Text>
          <Text>Updated: {result.updated}</Text>
          <Text>Skipped: {result.skipped}</Text>
          {result.newFields?.length > 0 && (
            <Text>New custom fields added: {result.newFields.map(f => f.label).join(', ')}</Text>
          )}
          {result.structureCreated && (
            <View style={{ marginTop: 8 }}>
              {result.structureCreated.categories?.length > 0 && (
                <Text>New categories: {result.structureCreated.categories.join(', ')}</Text>
              )}
              {result.structureCreated.families?.length > 0 && (
                <Text>New families: {result.structureCreated.families.join(', ')}</Text>
              )}
              {result.structureCreated.lines?.length > 0 && (
                <Text>New product lines: {result.structureCreated.lines.join(', ')}</Text>
              )}
            </View>
          )}
          {result.errors?.length > 0 && (
            <View style={{ marginTop: 8 }}>
              <Text style={{ color: '#b91c1c', fontWeight: '600' }}>{result.errors.length} row error(s):</Text>
              {result.errors.slice(0, 10).map((e: any, i: number) => (
                <Text key={i} style={{ color: '#b91c1c', fontSize: 12 }}>
                  Row {e.row >= 0 ? e.row : '-'}: {e.error}
                </Text>
              ))}
              {result.errors.length > 10 && <Text style={{ fontSize: 12 }}>…and {result.errors.length - 10} more</Text>}
            </View>
          )}
        </View>
      );
    }

    if (!rows.length) {
      return (
        <View style={{ alignItems: 'center', paddingVertical: 30 }}>
          {Platform.OS !== 'web' ? (
            <Text>Import is only available on the web platform.</Text>
          ) : (
            <>
              <TouchableOpacity style={styles.pickFile} onPress={() => fileInputRef.current?.click()}>
                <FontAwesome5 name="file-upload" size={18} color="#333" />
                <Text style={{ marginTop: 6 }}>{fileName || 'Choose a .csv or .xlsx file'}</Text>
              </TouchableOpacity>
              <input
                type="file"
                ref={fileInputRef}
                accept=".csv,.xlsx,.xls"
                style={{ display: 'none' }}
                onChange={handleFile}
              />
            </>
          )}
        </View>
      );
    }

    return (
      <View>
        <Text style={styles.sectionTitle}>{fileName} — {rows.length} rows</Text>
        <Text style={styles.hint}>
          Match each file column to a product field, an existing custom field, or create a new custom field.
        </Text>

        <View style={[styles.mapRow, styles.mapHeader]}>
          <Text style={[styles.mapCell, { width: 110, fontWeight: '700' }]}>File column</Text>
          <Text style={[styles.mapCell, { flex: 1, fontWeight: '700' }]}>Sample values</Text>
          <Text style={[styles.mapCell, { width: 175, fontWeight: '700' }]}>Import as</Text>
          <Text style={[styles.mapCell, { width: 140, fontWeight: '700' }]}>New type</Text>
        </View>
        {mappings.map((m, i) => (
          <View key={i} style={styles.mapRow}>
            <Text style={[styles.mapCell, { width: 110 }]} numberOfLines={1}>{m.column}</Text>
            <Text style={[styles.mapCell, { flex: 1, color: '#666' }]} numberOfLines={1}>{sampleFor(m.column)}</Text>
            <View style={[styles.mapCell, { width: 175 }]}>
              <Picker
                selectedValue={m.target}
                onValueChange={v => updateMapping(m.column, { target: v })}
                style={styles.picker}
              >
                <Picker.Item label="— Skip —" value="" />
                <Picker.Item label="— New custom field —" value="new" />
                {productFields?.length > 0 && <Picker.Item label="── Custom fields ──" value="__d1" enabled={false} />}
                {(productFields || []).map(f => (
                  <Picker.Item key={f.id} label={`${f.label} (${FIELD_TYPE_LABELS[f.field_type] || f.field_type})`} value={`meta:${f.id}`} />
                ))}
                <Picker.Item label="── Product fields ──" value="__d2" enabled={false} />
                {CORE_TARGETS.map(t => (
                  <Picker.Item key={t.value} label={t.label} value={t.value} />
                ))}
              </Picker>
            </View>
            <View style={[styles.mapCell, { width: 140 }]}>
              {m.target === 'new' ? (
                <Picker
                  selectedValue={m.newType}
                  onValueChange={v => updateMapping(m.column, { newType: v })}
                  style={styles.picker}
                >
                  {Object.keys(FIELD_TYPE_LABELS).map(t => (
                    <Picker.Item key={t} label={FIELD_TYPE_LABELS[t]} value={t} />
                  ))}
                </Picker>
              ) : <Text style={{ color: '#bbb' }}>—</Text>}
            </View>
          </View>
        ))}

        <View style={{ marginTop: 14 }}>
          <Text style={styles.sectionTitle}>Import options</Text>
          <View style={styles.optRow}>
            <Checkbox value={options.updateExisting} onValueChange={v => setOptions(o => ({ ...o, updateExisting: v }))} />
            <Text style={styles.optLabel}>Update existing products (matched by barcode)</Text>
          </View>
          <View style={styles.optRow}>
            <Checkbox value={options.createNew} onValueChange={v => setOptions(o => ({ ...o, createNew: v }))} />
            <Text style={styles.optLabel}>Create new products when no barcode match exists</Text>
          </View>
          <View style={styles.optRow}>
            <Checkbox value={options.metaOnly} onValueChange={v => setOptions(o => ({ ...o, metaOnly: v }))} />
            <Text style={styles.optLabel}>Import custom field values only (leave core product fields untouched)</Text>
          </View>
          <View style={styles.optRow}>
            <Checkbox value={options.createStructure} onValueChange={v => setOptions(o => ({ ...o, createStructure: v }))} />
            <Text style={styles.optLabel}>Create missing categories / families / product lines (uses each row's values as defaults)</Text>
          </View>
          {options.createStructure && (
            <View style={styles.optRow}>
              <Text style={[styles.optLabel, { marginLeft: 0 }]}>Default price group for new lines:</Text>
              <Picker
                selectedValue={defaultPriceGroup}
                onValueChange={v => setDefaultPriceGroup(v)}
                style={[styles.picker, { width: 220 }]}
              >
                <Picker.Item label="— Select —" value={null} />
                {priceGroups.map(g => (
                  <Picker.Item key={g.id} label={g.price_group} value={g.id} />
                ))}
              </Picker>
            </View>
          )}
        </View>
      </View>
    );
  };

  return isModalVisible ? (
    <View style={{ position: 'absolute', width: '100%', height: '100%' }}>
      <BasicModalContainer contentStyle={{ width: '92%', maxWidth: 780 }}>
        <ModalHeader label="Import Inventory" closeModal={closeModal} />
        <ModalBody>
          <ScrollView style={{ maxHeight: 480 }}>{body()}</ScrollView>
        </ModalBody>
        <ModalFooter>
          {!result && rows.length > 0 && (
            <BOHButton
              label={importing ? `Importing${progress && progress.total > 1 ? ` batch ${progress.done}/${progress.total}` : '…'}` : `Import ${rows.length} rows`}
              onPress={runImport}
              disabled={importing}
            />
          )}
          {result && (
            <BOHButton label="Close" onPress={closeModal} />
          )}
        </ModalFooter>
      </BasicModalContainer>
      {importing && (
        <View style={styles.loadingOverlay}>
          <ActivityIndicator size="large" color="#2563eb" />
        </View>
      )}
    </View>
  ) : null;
};

const styles = StyleSheet.create({
  ...(commonModalStyle as any),
  pickFile: {
    borderWidth: 1, borderStyle: 'dashed', borderColor: '#999', borderRadius: 8,
    padding: 30, alignItems: 'center', minWidth: 300,
  },
  sectionTitle: { fontWeight: '700', marginBottom: 6 },
  hint: { color: '#666', fontSize: 12, marginBottom: 10 },
  mapRow: { flexDirection: 'row', alignItems: 'center', borderBottomWidth: 1, borderBottomColor: '#eee', paddingVertical: 4 },
  mapHeader: { borderBottomColor: '#ccc' },
  mapCell: { paddingHorizontal: 4, fontSize: 13 },
  picker: { height: 30, fontSize: 12 },
  optRow: { flexDirection: 'row', alignItems: 'center', marginVertical: 4 },
  optLabel: { marginLeft: 8 },
  resultTitle: { fontWeight: '700', fontSize: 16, marginBottom: 8 },
  loadingOverlay: {
    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
    alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255,255,255,0.6)',
  },
});

export default ImportProductsModal;
